import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  User,
  Fingerprint,
  KeyRound,
  ShieldCheck,
  Activity,
  Zap,
  ArrowRight,
  ExternalLink,
  Download,
  CircleAlert,
} from 'lucide-react';

// ─── The six-layer KYA framework ─────────────────────────────────
const LAYERS = [
  {
    n: 1,
    icon: User,
    title: 'Principal Identity',
    ask: 'Who is the underlying customer?',
    kojiru: 'Deliberately not our layer. KYPA does not attempt to replace or duplicate PSP/bank KYC. The verified principal remains with the customer\u2019s existing financial institution. KYPA sits above that identity.',
    ours: false,
    verdict: 'Not our layer',
    accent: 'slate',
  },
  {
    n: 2,
    icon: Fingerprint,
    title: 'Agent Registration',
    ask: 'Which agent is acting, and how is it linked to the principal?',
    kojiru: 'KYPA Directory. Each enrolled agent receives a unique KYPA ID bound cryptographically to a Base wallet. The wallet is bound to a principal via one of six verification signals. Registry is public, on-chain, and readable by any indexer.',
    ours: true,
    verdict: 'Shipped',
    proof: { label: 'View KYPA Directory', href: '/kypa/directory', internal: true },
    accent: 'rose',
  },
  {
    n: 3,
    icon: KeyRound,
    title: 'Delegated Authority',
    ask: 'What has the customer authorized the agent to do?',
    kojiru: 'Pledge tier (Bronze / Silver / Gold / Platinum) plus verification signal count. Each tier maps to a maximum permitted transaction band and a mandatory SLA schema.',
    ours: true,
    verdict: 'Shipped',
    proof: { label: 'See pledge tiers', href: '/kypa#tiers', internal: true },
    accent: 'rose',
  },
  {
    n: 4,
    icon: ShieldCheck,
    title: 'Transaction Constraints',
    ask: 'What are the mandate boundaries?',
    kojiru: 'Pledge amount in USDC, endpoint URL, permitted categories, and per-agent SLA hooks executable by the ACS Hook contract. Constraints are checked on-chain, and a transaction exceeding them is recorded as a public breach record.',
    ours: true,
    verdict: 'Shipped',
    proof: { label: 'ACS Hook on BaseScan', href: 'https://basescan.org/address/0xaaae9ae4d52fe13384522e0ef4114b0a5f5d62d68', internal: false },
    accent: 'rose',
  },
  {
    n: 5,
    icon: Activity,
    title: 'Continuous Verification',
    ask: 'Is the agent still acting consistently with authorization?',
    kojiru: 'ACS\u2122 score (300\u2013850 band): deterministic, event-sourced, updated per attestation. Public breach records. Both are indexer-friendly and readable by any monitoring system.',
    ours: true,
    verdict: 'Shipped',
    proof: { label: 'See a live profile', href: '/kypa/agent/KYPA-aaae9a-13a7', internal: true },
    accent: 'rose',
  },
  {
    n: 6,
    icon: Zap,
    title: 'Revocation and Escalation',
    ask: 'Can authority be interrupted immediately?',
    kojiru: 'Breach recorded publicly next to the pledge, a public commitment signal. No custody. Not insurance. Delisting is a permissionless action by the ACS Hook contract when the pledge is exhausted.',
    ours: true,
    verdict: 'Shipped',
    proof: { label: 'Read the ACS Hook brief', href: '/downloads/kojiru-x402-kypa-brief.pdf', internal: true },
    accent: 'rose',
  },
];

const GOVERNANCE = [
  { level: 'L1 · Assist', tier: 'Bronze', pledge: '$1,500 \u2013 $10,000', fit: 'Read-only agents, information retrieval' },
  { level: 'L2 · Recommend', tier: 'Bronze / Silver', pledge: '$10,000 \u2013 $30,000', fit: 'Prepared transactions, human approves' },
  { level: 'L3 · Execute', tier: 'Gold', pledge: '$30,000 \u2013 $100,000', fit: 'Autonomous within limits' },
  { level: 'L4 · Exercise Discretion', tier: 'Platinum', pledge: '$100,000+', fit: 'Broadest autonomy, biggest pledge' },
];

const AccentText = ({ accent, children, className = '' }) => {
  const map = {
    slate: 'text-slate-400',
    rose: 'text-rose-300',
  };
  return <span className={`${map[accent]} ${className}`}>{children}</span>;
};

export default function KYA() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Helmet>
        <title>Six-Layer KYA Mapping · Kojiru</title>
        <meta
          name="description"
          content="How Kojiru KYPA maps to the framework author's Know Your Agent framework. Layer by layer. On-chain. For compliance and risk teams."
        />
        <meta property="og:title" content="Six-Layer KYA Mapping · Kojiru" />
        <meta
          property="og:description"
          content="The framework author's Agent Authority Stack, mapped to a live protocol. Five layers shipped. The sixth extends the framework from control to remedy."
        />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-xs font-bold tracking-[2px] text-rose-400 mb-6">
              FOR COMPLIANCE &middot; PUBLIC &middot; AUGUST 2026
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
              Know Your Agent, layer by layer.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-4">
              The framework author&rsquo;s <em>Know Your Agent</em> whitepaper (August 2026) proposes that agentic payments
              require an extension of traditional KYC into six control layers governing delegated authority. This page
              is our layer-by-layer response.
            </p>
            <p className="text-slate-400 leading-relaxed max-w-3xl">
              We do not claim to answer every question in the framework. We claim to have shipped five of the six
              layers as a live on-chain protocol, and to have extended the sixth with pledged collateral: the piece
              that turns a control model into a payable one.
            </p>
          </motion.div>
        </div>
      </section>

      {/* At-a-glance scorecard */}
      <section className="py-14 px-6 border-b border-white/5 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">SCORECARD</div>
          <h2 className="text-2xl sm:text-3xl font-black mb-6 leading-tight">
            Five layers shipped. The first is deliberately not ours.
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {LAYERS.map((l) => (
              <motion.div
                key={l.n}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: l.n * 0.05 }}
                className={`rounded-lg p-4 border ${
                  l.ours
                    ? 'bg-rose-500/[0.06] border-rose-500/30'
                    : 'bg-white/[0.02] border-white/10'
                }`}
                data-testid={`scorecard-layer-${l.n}`}
              >
                <div className="text-[9px] font-bold tracking-[1.5px] text-slate-500 mb-1">LAYER {l.n}</div>
                <div className="text-sm font-bold text-white mb-2 leading-tight">{l.title}</div>
                <AccentText accent={l.accent} className="text-[10px] font-bold tracking-[1.2px] uppercase">
                  {l.verdict}
                </AccentText>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Layer-by-layer */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-14">
          {LAYERS.map((l) => (
            <motion.div
              key={l.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5 }}
              className={`border-l-2 pl-6 sm:pl-10 ${
                l.ours ? 'border-rose-500/50' : 'border-white/10'
              }`}
              data-testid={`kya-layer-${l.n}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <l.icon className={`w-5 h-5 ${l.ours ? 'text-rose-400' : 'text-slate-400'}`} />
                <div className="text-xs font-bold tracking-[2px] text-slate-500">
                  LAYER {l.n}
                </div>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black mb-5 leading-tight">{l.title}</h3>

              <div className="mb-5">
                <div className="text-[11px] font-bold tracking-[1.5px] text-slate-500 mb-2">
                  THE FRAMEWORKThe framework author&rsquo;S ASKrsquo;S ASK
                </div>
                <p className="text-slate-300 leading-relaxed italic">&ldquo;{l.ask}&rdquo;</p>
              </div>

              <div className="mb-5">
                <div className={`text-[11px] font-bold tracking-[1.5px] mb-2 ${
                  l.ours ? 'text-rose-400' : 'text-slate-400'
                }`}>
                  KOJIRU&rsquo;S ANSWER
                </div>
                <p className="text-slate-200 leading-relaxed">{l.kojiru}</p>
              </div>

              {l.proof && (
                <div className="pt-3">
                  {l.proof.internal ? (
                    <Link to={l.proof.href}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                        data-testid={`kya-proof-layer-${l.n}`}
                      >
                        {l.proof.label}
                        <ArrowRight className="w-3.5 h-3.5 ml-2" />
                      </Button>
                    </Link>
                  ) : (
                    <a href={l.proof.href} target="_blank" rel="noopener noreferrer">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                        data-testid={`kya-proof-layer-${l.n}`}
                      >
                        {l.proof.label}
                        <ExternalLink className="w-3.5 h-3.5 ml-2" />
                      </Button>
                    </a>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Governance hierarchy */}
      <section className="py-16 px-6 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">
            GOVERNANCE HIERARCHY
          </div>
          <h2 className="text-3xl sm:text-4xl font-black mb-4 leading-tight">
            The framework author&rsquo;s four levels, mapped to KYPA tiers.
          </h2>
          <p className="text-slate-400 mb-8 max-w-3xl">
            The framework author&rsquo;s framework proposes four levels of agent autonomy. Each maps cleanly to a Kojiru pledge tier
            and a pledge range.
          </p>
          <div className="border border-white/10 rounded-xl overflow-hidden bg-[#0f0f16]">
            <div className="grid grid-cols-[1.5fr,1fr,1.3fr,2fr] gap-x-4 px-4 sm:px-6 py-3 border-b border-white/10 bg-white/5 text-[10px] font-bold tracking-[1.5px] text-slate-400">
              <div>FRAMEWORK LEVEL</div>
              <div>KOJIRU TIER</div>
              <div>PLEDGE RANGE</div>
              <div>BEST FIT</div>
            </div>
            {GOVERNANCE.map((g, i) => (
              <div
                key={i}
                className="grid grid-cols-[1.5fr,1fr,1.3fr,2fr] gap-x-4 px-4 sm:px-6 py-4 items-center border-b border-white/5 last:border-b-0 text-sm"
                data-testid={`governance-row-${i}`}
              >
                <div className="text-white font-semibold">{g.level}</div>
                <div className="text-rose-300 font-mono text-xs">{g.tier}</div>
                <div className="text-slate-300 font-mono text-xs">{g.pledge}</div>
                <div className="text-slate-400">{g.fit}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The seventh question */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <CircleAlert className="w-5 h-5 text-amber-400" />
            <div className="text-xs font-bold tracking-[2px] text-amber-400">THE SEVENTH QUESTION</div>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black mb-6 leading-tight">
            The framework author asks five closing questions. There is a sixth he doesn&rsquo;t quite ask.
          </h2>
          <p className="text-2xl text-slate-200 italic leading-relaxed mb-6 border-l-2 border-amber-400/40 pl-6">
            &ldquo;Who pays when the mandate is exceeded?&rdquo;
          </p>
          <p className="text-slate-300 leading-relaxed mb-4">
            Kojiru makes the answer visible. Every pledged agent posts collateral on-chain as a public commitment
            signal, and every proven breach is recorded next to it. Kojiru does not assume liability, is not
            insurance, and does not promise repayment.
          </p>
          <p className="text-slate-400 leading-relaxed">
            The first five layers of the framework tell you <em>whether</em> the mandate was breached. The sixth tells
            you <em>who moves</em> when it is. Without the sixth, an agent misbehaving is a signal. With the sixth, it
            is a settled account.
          </p>
        </div>
      </section>

      {/* Who this page is for */}
      <section className="py-16 px-6 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black mb-6 leading-tight">
            Who this page is for
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-[#0f0f16] border border-white/10 rounded-lg p-6">
              <div className="text-[10px] tracking-[2px] text-cyan-400 mb-2">COMPLIANCE OFFICERS</div>
              <p className="text-sm text-slate-300 leading-relaxed">
                A layer-by-layer artifact you can point at when your risk committee asks how the agent-authority stack
                is controlled.
              </p>
            </div>
            <div className="bg-[#0f0f16] border border-white/10 rounded-lg p-6">
              <div className="text-[10px] tracking-[2px] text-cyan-400 mb-2">AUDITORS</div>
              <p className="text-sm text-slate-300 leading-relaxed">
                Public on-chain evidence for every layer 2 through 6. Signed attestations. Deterministic scoring.
                Replayable forever.
              </p>
            </div>
            <div className="bg-[#0f0f16] border border-white/10 rounded-lg p-6">
              <div className="text-[10px] tracking-[2px] text-cyan-400 mb-2">PSP / BANK BD</div>
              <p className="text-sm text-slate-300 leading-relaxed">
                Kojiru extends your existing KYC without duplicating it. Layer 1 stays with you. Layers 2 through 6
                sit above it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Close + PDF */}
      <section className="py-16 px-6 border-t border-white/5 bg-gradient-to-b from-transparent to-rose-950/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-6 leading-tight">
            Five layers on-chain. The sixth makes them payable.
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            If a claim on this page overreaches, tell us where. If your compliance stack needs a specific artifact we
            have not published, tell us which. We will fix it before anyone else reads.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/downloads/kojiru-sheehy-kya-response.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                className="bg-white text-black hover:bg-slate-100"
                data-testid="download-kya-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download the mapping (PDF)
              </Button>
            </a>
            <a href="mailto:wayne@kojiru.com">
              <Button
                size="lg"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5"
                data-testid="email-founder-btn"
              >
                wayne@kojiru.com
              </Button>
            </a>
          </div>
          <p className="text-xs text-slate-600 mt-8">
            Framework: <em>Know Your Agent.</em> Industry white paper from a global payments company, August 2026.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
